/**
 * The decision-tree API, implemented in the browser against localStorage.
 *
 * It enforces the rules the service will enforce — see `DecisionTreeApi` for why that matters
 * — and models the two DynamoDB tables directly:
 *
 *   versions   (treeId, version)  with version 0 holding the tree's metadata
 *   drafts     (treeId, owner)
 *
 * so the conditional writes here are the same conditions the DAO will use. The commit is the
 * one that matters: it writes version `base + 1` only if that version does not exist, which
 * IS the concurrent-write detection rather than a check bolted beside one.
 *
 * ## Simulating a second person
 *
 * The behaviours that are hardest to build — the "someone published" banner, the conflict
 * dialog — need another editor, and there is only one browser. So there are two dev hooks,
 * reachable from the console:
 *
 *   window.__cpaMock.otherEditorOpens()   another person forks the head
 *   window.__cpaMock.otherEditorCommits() another person commits a version
 *   window.__cpaMock.reset()              wipe and reseed from the fixtures
 *
 * They are on `window` rather than in the UI on purpose: they are not features, and a button
 * labelled "pretend someone else saved" would end up in a screenshot.
 */
import { SEED_BY_ID } from '../fixtures/questionnaires';
import { SCHEMA_VERSION, serializeDocument, toDocument } from '../utils/wire';
import {
  ConflictError,
  type DecisionTreeApi,
  DecisionTreeConflictError,
  type DecisionTreeDraftData,
  type DecisionTreeEditingContext,
  type DecisionTreeEditor,
  DecisionTreeValidationError,
  type DecisionTreeVersionData,
  type DecisionTreeVersionSummary,
  NotFoundError,
  validateDocument,
} from './treeApiTypes';

const KEY = 'cpa.mock.decisionTree.v1';

/** Who the mock thinks is calling. The real client takes this from the id token. */
export const MOCK_OWNER = 'liukp';

interface TreeMeta {
  treeId: string;
  activeVersion?: number;
  displayName: string;
}

interface Store {
  meta: Record<string, TreeMeta>;
  versions: Record<string, DecisionTreeVersionData[]>;
  drafts: Record<string, DecisionTreeDraftData[]>;
}

/* ── Storage ────────────────────────────────────────────────────────────────── */

function seed(treeId: string): Store {
  // Version 1 is the tree as it exists today, so the history starts from something real
  // rather than from an empty canvas.
  const { document } = toDocument(SEED_BY_ID[treeId] ?? SEED_BY_ID['q_all'], {});
  const now = new Date().toISOString();
  return {
    meta: { [treeId]: { treeId, activeVersion: 1, displayName: 'Credit Program Advisor 主决策树' } },
    versions: {
      [treeId]: [
        {
          treeId,
          version: 1,
          document: serializeDocument(document),
          schemaVersion: SCHEMA_VERSION,
          status: 'published',
          note: '初始版本：从前端 fixtures 导入',
          author: 'fayewu',
          createdAt: now,
          updatedAt: now,
          publishedBy: 'fayewu',
          publishedAt: now,
        },
      ],
    },
    drafts: { [treeId]: [] },
  };
}

/**
 * The store, seeding this tree if it is not in there yet.
 *
 * The seed is MERGED into whatever is already persisted. `seed()` returns a store scoped to
 * one treeId, so writing it wholesale replaced the single `KEY` and took every other tree's
 * versions and drafts with it — opening tree B erased tree A. One tree exists today, but
 * supporting several was an explicit requirement when the tables were designed, so the bug
 * would have surfaced exactly when the second tree arrived and looked like data loss in the
 * real service rather than in this mock.
 */
function read(treeId: string): Store {
  let store: Store | undefined;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) store = JSON.parse(raw) as Store;
  } catch {
    // A corrupt store is reseeded rather than thrown: this is scaffolding, and leaving the
    // page unusable because of a stale localStorage shape would waste more time than it saves.
  }
  if (store?.versions?.[treeId]?.length) return store;

  const fresh = seed(treeId);
  // Existing trees first, the seeded one layered on top: only `treeId`'s entries are new, so
  // spreading `fresh` second adds it without touching the others.
  const merged: Store = store
    ? {
        meta: { ...store.meta, ...fresh.meta },
        versions: { ...store.versions, ...fresh.versions },
        drafts: { ...store.drafts, ...fresh.drafts },
      }
    : fresh;
  write(merged);
  return merged;
}

function write(s: Store): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // Quota. The mock keeps working from memory for this page load.
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** A little latency, so the UI is built against async rather than against instant. */
const LATENCY_MS = 120;

/* ── Helpers ────────────────────────────────────────────────────────────────── */

const versionsOf = (s: Store, treeId: string) => s.versions[treeId] ?? [];
const draftsOf = (s: Store, treeId: string) => s.drafts[treeId] ?? [];
const headOf = (s: Store, treeId: string) =>
  versionsOf(s, treeId).reduce((m, v) => Math.max(m, v.version), 0);

function contextOf(s: Store, treeId: string, owner: string): DecisionTreeEditingContext {
  const others: DecisionTreeEditor[] = draftsOf(s, treeId)
    .filter((d) => d.owner !== owner)
    .map((d) => ({ owner: d.owner, baseVersion: d.baseVersion, updatedAt: d.updatedAt }));
  return {
    headVersion: headOf(s, treeId),
    activeVersion: s.meta[treeId]?.activeVersion,
    otherEditors: others,
  };
}

function summaryOf(v: DecisionTreeVersionData): DecisionTreeVersionSummary {
  const { document: _document, ...rest } = v;
  return rest;
}

async function sha256(json: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(json));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/* ── The implementation ─────────────────────────────────────────────────────── */

export class MockDecisionTreeApi implements DecisionTreeApi {
  constructor(private readonly owner: string = MOCK_OWNER) {}

  async createDraft({ treeId, baseVersion }: { treeId: string; baseVersion?: number }) {
    await sleep(LATENCY_MS);
    const s = read(treeId);
    if (draftsOf(s, treeId).some((d) => d.owner === this.owner)) {
      // Not "overwrite it": that draft is unsaved work, and a page load is the worst moment
      // to discard it.
      throw new ConflictError('你已经有一份草稿了');
    }
    const from = baseVersion ?? headOf(s, treeId);
    const version = versionsOf(s, treeId).find((v) => v.version === from);
    if (!version) throw new NotFoundError(`版本 ${from} 不存在`);

    const now = new Date().toISOString();
    const draft: DecisionTreeDraftData = {
      treeId,
      owner: this.owner,
      // Forked server-side: the 30 KB document is never downloaded only to be sent back.
      document: version.document,
      schemaVersion: version.schemaVersion,
      baseVersion: from,
      createdAt: now,
      updatedAt: now,
    };
    s.drafts[treeId] = [...draftsOf(s, treeId), draft];
    write(s);
    return { draft, context: contextOf(s, treeId, this.owner) };
  }

  async getDraft({ treeId }: { treeId: string }) {
    await sleep(LATENCY_MS);
    const s = read(treeId);
    const draft = draftsOf(s, treeId).find((d) => d.owner === this.owner);
    if (!draft) throw new NotFoundError('没有草稿');
    return { draft, context: contextOf(s, treeId, this.owner) };
  }

  async putDraft({
    treeId,
    document,
    schemaVersion,
    expectedUpdatedAt,
  }: {
    treeId: string;
    document: string;
    schemaVersion: number;
    expectedUpdatedAt: string;
  }) {
    await sleep(LATENCY_MS);
    const s = read(treeId);
    const draft = draftsOf(s, treeId).find((d) => d.owner === this.owner);
    if (!draft) throw new NotFoundError('没有草稿');
    if (draft.updatedAt !== expectedUpdatedAt) {
      // A second tab on the same draft. Overwriting blind loses whichever tab saved less
      // recently, silently, because both tabs believe they are saving.
      throw new ConflictError(
        `这份草稿在 ${draft.updatedAt} 被改过（你持有 ${expectedUpdatedAt}）。可能是另一个标签页。`,
      );
    }
    const next: DecisionTreeDraftData = {
      ...draft,
      document,
      schemaVersion,
      updatedAt: new Date().toISOString(),
    };
    s.drafts[treeId] = draftsOf(s, treeId).map((d) => (d.owner === this.owner ? next : d));
    write(s);
    return { draft: next, context: contextOf(s, treeId, this.owner) };
  }

  async deleteDraft({ treeId, expectedUpdatedAt }: { treeId: string; expectedUpdatedAt: string }) {
    await sleep(LATENCY_MS);
    const s = read(treeId);
    const draft = draftsOf(s, treeId).find((d) => d.owner === this.owner);
    if (!draft) return;
    if (draft.updatedAt !== expectedUpdatedAt) {
      throw new ConflictError('这份草稿在别处被改过，没有删除');
    }
    s.drafts[treeId] = draftsOf(s, treeId).filter((d) => d.owner !== this.owner);
    write(s);
  }

  async listEditors({ treeId }: { treeId: string }) {
    await sleep(LATENCY_MS);
    return { context: contextOf(read(treeId), treeId, this.owner) };
  }

  async commitVersion({
    treeId,
    expectedDraftUpdatedAt,
    documentSha256,
    baseVersion,
    note,
    mergedFrom,
  }: {
    treeId: string;
    expectedDraftUpdatedAt: string;
    documentSha256: string;
    baseVersion: number;
    note: string;
    mergedFrom?: number[];
  }) {
    await sleep(LATENCY_MS);
    const s = read(treeId);
    const draft = draftsOf(s, treeId).find((d) => d.owner === this.owner);
    if (!draft) throw new NotFoundError('没有草稿可提交');

    if (draft.updatedAt !== expectedDraftUpdatedAt) {
      throw new ConflictError('草稿在别处被改过，没有提交');
    }
    if (draft.baseVersion !== baseVersion) {
      throw new ConflictError(
        `草稿 fork 自 v${draft.baseVersion}，而你声明的是 v${baseVersion}`,
      );
    }

    // The hash check. Its whole reason for existing is the case the timestamp above cannot
    // see: a final autosave that silently failed leaves BOTH timestamps at the older value,
    // so they match and a stale document would be promoted with the newest edits lost —
    // reporting success the whole way.
    const actual = await sha256(draft.document);
    if (actual !== documentSha256) {
      throw new ConflictError(
        '你屏幕上的内容和已保存的草稿不一致 —— 最后一次自动保存可能失败了。请重试保存。',
      );
    }

    const problems = validateDocument(JSON.parse(draft.document));
    if (problems.length) {
      throw new DecisionTreeValidationError(
        `这棵树有 ${problems.length} 个问题，什么都没有保存。`,
        problems,
      );
    }

    // The conditional write: version base+1 only if it does not exist.
    const target = baseVersion + 1;
    const taken = versionsOf(s, treeId).find((v) => v.version === target);
    if (taken) {
      const head = versionsOf(s, treeId).reduce((a, b) => (a.version > b.version ? a : b));
      throw new DecisionTreeConflictError(
        `v${target} 已被 ${taken.author ?? '他人'} 占用。你的改动没有丢，仍在草稿里。`,
        head.version,
        head.author,
        head.createdAt,
      );
    }

    const now = new Date().toISOString();
    const version: DecisionTreeVersionData = {
      treeId,
      version: target,
      document: draft.document,
      schemaVersion: draft.schemaVersion,
      status: 'draft',
      baseVersion,
      mergedFrom,
      documentSha256,
      note,
      author: this.owner,
      createdAt: now,
      updatedAt: now,
    };
    s.versions[treeId] = [...versionsOf(s, treeId), version];
    s.drafts[treeId] = draftsOf(s, treeId).filter((d) => d.owner !== this.owner);
    write(s);
    return { version, draftDeleted: true };
  }

  async getVersion({ treeId, version }: { treeId: string; version: number }) {
    await sleep(LATENCY_MS);
    const found = versionsOf(read(treeId), treeId).find((v) => v.version === version);
    if (!found) throw new NotFoundError(`版本 ${version} 不存在`);
    return { version: found };
  }

  async listVersions({ treeId, maxResults = 20 }: { treeId: string; maxResults?: number }) {
    await sleep(LATENCY_MS);
    const all = [...versionsOf(read(treeId), treeId)].sort((a, b) => b.version - a.version);
    return { versions: all.slice(0, maxResults).map(summaryOf) };
  }

  async publishVersion({
    treeId,
    version,
    expectedUpdatedAt,
  }: {
    treeId: string;
    version: number;
    expectedUpdatedAt: string;
  }) {
    await sleep(LATENCY_MS);
    const s = read(treeId);
    const found = versionsOf(s, treeId).find((v) => v.version === version);
    if (!found) throw new NotFoundError(`版本 ${version} 不存在`);
    if (found.updatedAt !== expectedUpdatedAt) {
      throw new ConflictError('这个版本在别处被改过，没有发布');
    }
    const now = new Date().toISOString();
    s.versions[treeId] = versionsOf(s, treeId).map((v) =>
      v.version === version
        ? { ...v, status: 'published' as const, publishedBy: this.owner, publishedAt: now, updatedAt: now }
        : v.status === 'published'
          ? { ...v, status: 'archived' as const }
          : v,
    );
    s.meta[treeId] = { ...(s.meta[treeId] ?? { treeId, displayName: treeId }), activeVersion: version };
    write(s);
    return { version: versionsOf(s, treeId).find((v) => v.version === version)! };
  }
}

/* ── Dev hooks ──────────────────────────────────────────────────────────────── */

/**
 * Install the "another person" controls on `window`.
 *
 * Called once from `getDecisionTreeApi`. Not conditional on an env flag: this whole module is
 * scaffolding that goes away with the real client, and a flag would be one more thing to
 * remember to remove.
 */
export function installMockHooks(treeId: string): void {
  const other = 'fayewu';
  const api = new MockDecisionTreeApi(other);
  const hooks = {
    /** Another person forks the head, so `otherEditors` is non-empty. */
    async otherEditorOpens() {
      try {
        const { draft } = await api.createDraft({ treeId });
        // eslint-disable-next-line no-console
        console.log(`[cpaMock] ${other} 开始编辑，基于 v${draft.baseVersion}`);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(`[cpaMock] ${other} 已经在编辑了`, e);
      }
    },
    /** Another person commits, so the head moves and your base goes stale. */
    async otherEditorCommits() {
      const s = read(treeId);
      let draft = draftsOf(s, treeId).find((d) => d.owner === other);
      if (!draft) draft = (await api.createDraft({ treeId })).draft;
      const json = draft.document;
      const { version } = await api.commitVersion({
        treeId,
        expectedDraftUpdatedAt: draft.updatedAt,
        documentSha256: await sha256(json),
        baseVersion: draft.baseVersion,
        note: `${other} 的改动`,
      });
      // eslint-disable-next-line no-console
      console.log(`[cpaMock] ${other} 提交了 v${version.version} —— 刷新或等自动保存，应看到黄条`);
    },
    /** Wipe and reseed from the fixtures. */
    reset() {
      localStorage.removeItem(KEY);
      // eslint-disable-next-line no-console
      console.log('[cpaMock] 已重置，刷新页面');
    },
    /** Dump the whole store, to read the version history and drafts. */
    dump() {
      const s = read(treeId);
      // eslint-disable-next-line no-console
      console.log('[cpaMock] store', {
        meta: s.meta[treeId],
        versions: versionsOf(s, treeId).map(summaryOf),
        drafts: draftsOf(s, treeId).map(({ document: _d, ...rest }) => rest),
      });
      return s;
    },
  };
  (window as unknown as { __cpaMock?: typeof hooks }).__cpaMock = hooks;
}
